/**
 * SignerList Component
 * Displays the list of signers on the left side
 */

import { i18n } from '../utils/i18n.js';

const DEFAULT_GROUP_ID = '__default__';

export class SignerList {
  constructor({ container, state, onSignerSignClick, onSignerSelect, isLocked = false, signerLists = [] }) {
    this.container = container;
    this.state = state;
    this.onSignerSignClick = onSignerSignClick;
    this.onSignerSelect = onSignerSelect;
    this.isLocked = isLocked;
    this.signerLists = signerLists;
    this.selectedSignerIndex = null;
  }

  render() {
    const signers = this.state.getSigners();
    
    // Group signers by signerListId
    const groups = this.groupSigners(signers);

    this.container.innerHTML = `
      <div class="h-full bg-white rounded-lg shadow-lg lg:shadow-sm border border-gray-200 flex flex-col">
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-800">${i18n.t('signers.title')}</h2>
          <button id="wsp-close-sidebar" class="lg:hidden p-1 hover:bg-gray-100 rounded-md transition-colors">
            <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="flex-1 overflow-y-auto">
          ${groups.map(group => this.renderGroup(group)).join('')}
        </div>
      </div>
    `;

    // Attach event listeners
    this.attachEventListeners();
    this.attachGroupListeners();
    this.attachCloseButton();
  }

  groupSigners(signers) {
    const grouped = {};
    
    signers.forEach(signer => {
      const groupId = signer.signerListId || DEFAULT_GROUP_ID;
      if (!grouped[groupId]) {
        // Find signerList config for this group
        const signerListConfig = this.signerLists.find(list => list.listId === groupId);
        
        grouped[groupId] = {
          id: groupId,
          label: signerListConfig?.label || i18n.t('signers.defaultGroup'),
          isExpanded: signerListConfig?.isExpanded !== false, // Default to true if not specified
          signers: []
        };
      }
      grouped[groupId].signers.push(signer);
    });

    return Object.values(grouped);
  }

  renderGroup(group) {
    const chevronIcon = group.isExpanded 
      ? '<svg class="w-5 h-5 transition-transform" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>'
      : '<svg class="w-5 h-5 transition-transform -rotate-90" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>';

    return `
      <div class="border-b border-gray-100 last:border-b-0">
        <button 
          class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
          data-group-id="${group.id}"
        >
          <span class="text-sm font-semibold text-gray-700">${this.escapeHtml(group.label)}</span>
          ${chevronIcon}
        </button>
        <div class="px-2 pb-2 ${group.isExpanded ? '' : 'hidden'}" data-group-content="${group.id}">
          ${group.signers.map(signer => this.renderSignerItem(signer)).join('')}
        </div>
      </div>
    `;
  }

  renderSignerItem(signer) {
    const isSelected = this.selectedSignerIndex === signer._index;
    const bgClass = signer.isSigned 
      ? 'bg-green-50 border-green-200 hover:bg-green-100' 
      : isSelected 
        ? 'bg-blue-50 border-blue-400 shadow-md' 
        : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-primary-400 hover:shadow-md';
    
    const mandatoryBadge = signer.isMandatory 
      ? `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">${i18n.t('signers.mandatory')}</span>` 
      : '';
    
    const statusIcon = signer.isSigned 
      ? '<svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>'
      : '<svg class="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/></svg>';

    // Determine if button should be disabled
    const buttonHtml = !signer.isSigned 
      ? `<button 
          class="flex-shrink-0 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed" 
          ${this.isLocked ? 'disabled' : ''}
          data-sign-button
        >
          ${i18n.t('button.sign')}
        </button>` 
      : '';

    return `
      <div class="flex items-start gap-3 p-3 mb-2 rounded-lg border transition-all duration-200 cursor-pointer ${bgClass}" data-signer-index="${signer._index}">
        <div class="flex-shrink-0 mt-0.5">
          ${statusIcon}
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-gray-900 line-clamp-2">${this.escapeHtml(signer.label)}</div>
          <div class="flex items-center gap-2 mt-1">
            ${mandatoryBadge}
            ${signer.isSigned ? `<span class="text-xs text-gray-500">${this.formatTimestamp(signer.timestamp)}</span>` : ''}
          </div>
        </div>
        ${buttonHtml}
      </div>
    `;
  }

  attachEventListeners() {
    const signerItems = this.container.querySelectorAll('[data-signer-index]');
    
    signerItems.forEach(item => {
      const signerIndex = parseInt(item.dataset.signerIndex, 10);
      const signer = this.state.getSigner(signerIndex);
      
      // Handle row click for selection
      item.addEventListener('click', (e) => {
        // Don't select if clicking the sign button
        if (e.target.closest('[data-sign-button]')) {
          return;
        }
        
        // Update selected state
        const previousIndex = this.selectedSignerIndex;
        this.selectedSignerIndex = signerIndex;
        
        // Update visual state without re-rendering
        if (previousIndex !== null) {
          const prevItem = this.container.querySelector(`[data-signer-index="${previousIndex}"]`);
          if (prevItem && !this.state.getSigner(previousIndex)?.isSigned) {
            prevItem.classList.remove('bg-blue-50', 'border-blue-400', 'shadow-md');
            prevItem.classList.add('bg-white', 'border-gray-200', 'hover:bg-gray-50', 'hover:border-primary-400', 'hover:shadow-md');
          }
        }
        
        // Add selected styles to current item (only if not signed)
        if (!signer.isSigned) {
          item.classList.remove('bg-white', 'border-gray-200', 'hover:bg-gray-50', 'hover:border-primary-400', 'hover:shadow-md');
          item.classList.add('bg-blue-50', 'border-blue-400', 'shadow-md');
        }
        
        // Call selection callback
        if (this.onSignerSelect) {
          this.onSignerSelect(signer);
        }
      });

      // Handle double click for quick sign (select + sign)
      item.addEventListener('dblclick', (e) => {
        // Don't trigger if clicking the sign button
        if (e.target.closest('[data-sign-button]')) {
          return;
        }
        
        // Only allow if not signed and not locked
        if (!signer.isSigned && !this.isLocked) {
          // Select the signer first
          this.selectedSignerIndex = signerIndex;
          if (this.onSignerSelect) {
            this.onSignerSelect(signer);
          }
          
          // Then trigger sign
          this.onSignerSignClick(signer);
        }
      });
      
      // Handle sign button click
      if (!signer.isSigned && !this.isLocked) {
        const signBtn = item.querySelector('[data-sign-button]');
        if (signBtn) {
          signBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.onSignerSignClick(signer);
          });
        }
      }
    });
  }

  attachGroupListeners() {
    const groupButtons = this.container.querySelectorAll('[data-group-id]');
    
    groupButtons.forEach(button => {
      button.addEventListener('click', () => {
        const groupId = button.dataset.groupId;
        const content = this.container.querySelector(`[data-group-content="${groupId}"]`);
        const chevron = button.querySelector('svg');
        
        if (content && chevron) {
          const isHidden = content.classList.contains('hidden');
          
          if (isHidden) {
            content.classList.remove('hidden');
            chevron.classList.remove('-rotate-90');
          } else {
            content.classList.add('hidden');
            chevron.classList.add('-rotate-90');
          }
        }
      });
    });
  }

  attachCloseButton() {
    const closeBtn = this.container.querySelector('#wsp-close-sidebar');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        // Trigger the same close action as clicking overlay
        const sidebar = document.querySelector('#wsp-sidebar');
        const overlay = document.querySelector('#wsp-overlay');
        
        if (sidebar && overlay) {
          sidebar.classList.add('-translate-x-full');
          overlay.classList.add('hidden');
        }
      });
    }
  }

  formatTimestamp(timestamp) {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
